"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef } from "react";
export default function PartnershipInquiryCta() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    let animationId: number;
    let scrollY = 0;
    const drawTexture = () => {
      if (canvas.width === 0 || canvas.height === 0) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gridSize = 60;
      const offset = scrollY * 0.3;
      ctx.strokeStyle = "hsla(215, 80%, 48%, 0.08)";
      ctx.lineWidth = 1;
      for (let x = -gridSize; x < canvas.width + gridSize; x += gridSize) {
        for (let y = -gridSize; y < canvas.height + gridSize; y += gridSize) {
          const adjustedY = (y + offset) % (canvas.height + gridSize * 2);
          ctx.beginPath();
          ctx.arc(x, adjustedY, 2, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    };
    const animate = () => {
      scrollY = window.scrollY;
      drawTexture();
      animationId = requestAnimationFrame(animate);
    };
    if (canvas.width > 0 && canvas.height > 0) {
      animate();
    }
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);
  const handleRipple = (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add("ripple-effect");
    button.appendChild(ripple);
    setTimeout(() => {
      ripple.remove();
    }, 600);
  };
  return <section className="relative overflow-hidden bg-dark-background py-24 md:py-32">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto text-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <div className="bg-primary/10 backdrop-blur-sm rounded-lg p-8 md:p-12 shadow-medium border border-primary/20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground-dark mb-6">
              Ready to Bring Your Vision to Life?
            </h2>
            <p className="text-lg md:text-xl text-foreground-dark/80 mb-8">
              Partner with Flex DPS to transform your innovative concepts into
              market-ready products. Let's discuss how our expertise can
              accelerate your success.
            </p>
            <Link to="/ContactPage">
              <Button size="lg" onClick={handleRipple} className="relative overflow-hidden bg-accent hover:bg-accent-hover active:bg-accent-active text-foreground-accent font-semibold px-8 py-4 rounded-md shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-[1.02] hover:shadow-strong focus-visible:ring-2 focus-visible:ring-accent">
                Start Your Partnership
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        .ripple-effect {
          position: absolute;
          border-radius: 50%;
          background: hsla(0, 0%, 100%, 0.5);
          transform: scale(0);
          animation: ripple-animation 600ms ease-out;
          pointer-events: none;
        }

        @keyframes ripple-animation {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `}</style>
    </section>;
}